import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/features/language';
import { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { useAppSelector } from '@/app/hooks';
import { selectUser } from '@/entities/user';
import { MenuList } from '@/entities/sidebar';
import { ProfitInfo } from '@/entities/profit';
import { Bell } from '@/entities/notification';
import { useLogout } from '@/features/auth';
import { selectIsSidebarCollapsed } from '@/features/sidebar';
import { brand } from '@/brand';
import { Icon } from '@/shared/IconCollecion';
import { Logo } from '@/shared/ui/Logo';

// 1:1 с mcms-front Sidebar. Sticky слева на lg+, MenuList + Logout-кнопка.
// При brand.layout.header === 'in-sidebar' дополнительно показывает Logo +
// User-link + Bell + ProfitInfo внутри сайдбара (иначе они в Header).
// При 'in-content' в сайдбаре только Logo: язык, баланс и профиль живут в
// строке аккаунта над контентом (AccountBar).
// Это структура, а не цвет, поэтому решение принимает бренд, не тема; фоновая
// картинка-планета ниже — наоборот, чистая эстетика темы.
//
// Адаптации:
// - logoutProcess → наш useLogout() из @/features/auth.
// - User-link ведёт на /settings (наша страница профиля).

const Sidebar = () => {
  const sidebarRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const isCollapsed = useAppSelector(selectIsSidebarCollapsed);
  const logout = useLogout();
  const isProfileActive = location.pathname === '/settings';

  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const rootStyle = document.documentElement.style;
    const getHeaderHeight = () => {
      const headerSize = getComputedStyle(document.documentElement)
        .getPropertyValue('--header-size')
        .trim();
      if (!headerSize) return 0;
      if (headerSize.endsWith('px')) return Number.parseFloat(headerSize) || 0;
      if (headerSize.endsWith('rem')) {
        const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
        return (Number.parseFloat(headerSize) || 0) * (rootFontSize || 16);
      }
      return Number.parseFloat(headerSize) || 0;
    };

    const updateSidebarTop = () => {
      const headerHeight = getHeaderHeight();
      const availableHeight = window.innerHeight - headerHeight;
      const nextTop = Math.min(0, availableHeight - sidebar.offsetHeight);
      rootStyle.setProperty('--sidebar-top', `${nextTop}px`);
    };

    const resizeObserver = new ResizeObserver(() => {
      updateSidebarTop();
    });

    updateSidebarTop();
    resizeObserver.observe(sidebar);
    window.addEventListener('resize', updateSidebarTop);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateSidebarTop);
      rootStyle.removeProperty('--sidebar-top');
    };
  }, []);

  const handleLogout = () => {
    // useLogout (RTK) синхронно чистит cookies + ставит guest, поэтому
    // SPA-navigate безопасен — RequireAuth увидит guest.
    logout();
    navigate('/login', { replace: true });
  };

  const { t } = useTranslation('common');
  const isHeaderInContent = brand.layout.header === 'in-content';
  const isHeaderInSidebar = brand.layout.header === 'in-sidebar';
  const isPills = brand.layout.sidebarMenu === 'pills';
  // Поля развёрнутого меню. Когда шапка (логотип, профиль) живёт в сайдбаре,
  // сверху поле меньше, а со стороны края окна — 8px вместо 16px (при меню
  // справа зеркально). Раньше это были префиксы t-green: поверх px-4 py-6 —
  // привязка к теме wap, а не к виду шапки.
  const expandedPadding = isHeaderInContent
    ? 'px-[18px] py-7'
    : isHeaderInSidebar
      ? cn('pb-6 pt-4', brand.layout.sidebar === 'right' ? 'pl-4 pr-2' : 'pl-2 pr-4')
      : 'px-4 py-6';

  return (
    <aside
      ref={sidebarRef}
      className={cn(
        // transition по width И min-width: иначе при разворачивании min-width
        // прыгает в aside-size мгновенно и перебивает анимацию (флекс сразу
        // растягивает блок). duration разная по направлению — скрытие чуть
        // медленнее (300ms), раскрытие быстрее (200ms), см. ternary ниже.
        'sticky hidden flex-col self-start bg-surface-sidebar bg-left-bottom bg-no-repeat text-content-inverse-muted transition-[width,min-width] ease-in-out lg:flex',
        isCollapsed
          ? cn('w-[64px] min-w-[64px] px-2 duration-300', isHeaderInSidebar ? 'pb-6 pt-4' : 'py-6')
          : cn('w-aside-size min-w-aside-size duration-200', expandedPadding),
      )}
      style={{
        // Декор подвала — токен темы (--sidebar-bg-image, у wap 'none'), чтобы
        // ассет не попадал в чужой бандл. При collapsed скрываем: мало места.
        backgroundImage: isCollapsed ? 'none' : 'var(--sidebar-bg-image)',
        minHeight: 'calc(100vh - var(--header-size))',
        top: 'calc(var(--header-size) + var(--sidebar-top, 0px))',
      }}
    >
      <div className="flex flex-1 flex-col">
        {isHeaderInContent && (
          <div className="mb-9 flex justify-start pl-2">
            <Logo min={false} />
          </div>
        )}

        {isHeaderInSidebar && (
          <>
            <div className="mb-4 flex justify-start pl-2">
              <Logo min={false} />
            </div>

            {brand.layout.sidebarProfile === 'link' && (
              <Link
                to="/settings"
                className={cn(
                  'mb-2 flex w-full items-center justify-start truncate py-1',
                  isProfileActive
                    ? 'text-content-accent lg:text-content-inverse-accent'
                    : 'text-content-primary hover:text-content-accent lg:text-content-inverse-muted lg:hover:text-content-inverse-accent',
                )}
              >
                <div className="flex flex-1 items-center gap-2 truncate pl-2">
                  <Icon name="User_Big" className="size-6 shrink-0" color="currentColor" />
                  <div className="mr-auto max-w-[calc(100%-1.5rem)] truncate font-bold">
                    <span>{user ? user.username : 'user_'}</span>
                  </div>
                </div>
              </Link>
            )}

            {brand.features.notifications && (
              <div className="mb-2 flex items-center gap-2 pl-2">
                <Bell className="w-full justify-start" showLabel />
              </div>
            )}

            {/* Язык — отдельный блок с чертой снизу, как у финансового ниже.
                У бренда без колокольчика (features.notifications: false) флаг
                стоял в строке один и читался как случайный элемент; строкой с
                названием языка он встаёт в ту же сетку, что пункты меню. */}
            <div className="mb-4 ml-2 border-b border-line-muted pb-4">
              <LanguageSwitcher variant="row" align="right" collapsed={isCollapsed} />
            </div>

            <div className="mb-4 pl-2">
              <ProfitInfo variant="menu" />
            </div>
          </>
        )}

        <div className="mb-4 flex min-h-0 flex-1 flex-col">
          <MenuList />
        </div>

        <button
          type="button"
          title={isCollapsed ? t('logout') : undefined}
          className={cn(
            'mt-auto flex w-full items-center overflow-hidden text-left text-sm leading-5',
            // Пилюли: «Выйти» той же метрики, что пункты меню, с подсветкой строки.
            isPills
              ? 'gap-2.5 rounded-control px-3.5 py-[11px] font-semibold text-content-inverse-muted transition-colors hover:bg-surface-sidebar-hover hover:text-content-inverse'
              : 'max-h-8 gap-2 py-1 pl-2 font-medium text-content-primary hover:text-content-accent lg:text-content-inverse-muted lg:hover:text-content-inverse-accent',
          )}
          onClick={handleLogout}
        >
          <Icon name="Log_Out" color="currentColor" className={cn('shrink-0', isPills && 'size-[18px]')} />
          {/* Подпись гаснет плавно (в DOM остаётся), синхронно с пунктами меню. */}
          <span
            className={cn(
              'whitespace-nowrap transition-opacity duration-200',
              isCollapsed ? 'opacity-0' : 'opacity-100',
            )}
          >
            {t('logout')}
          </span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
